import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook for managing input mode state and transitions
 * @param {Object} options - Configuration options
 * @param {Array} options.tags - Current tags
 * @param {string} options.joinDelimiter - Delimiter for joining tags
 * @param {number} options.limit - Tag limit
 * @returns {Object} Input mode state and handlers
 */
const useInputMode = ({ tags, joinDelimiter = ', ', limit = 100 }) => {
  const [inputValue, setInputValue] = useState('');
  const [isInputMode, setIsInputMode] = useState(false);
  const [currentTag, setCurrentTag] = useState('');

  const textareaRef = useRef(null);
  const isMountedRef = useRef(true);
  const timeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Render tags as input value
  const renderTagsToInput = useCallback(() => {
    const value = tags.map((tag) => tag.display || tag.text).join(joinDelimiter);
    return value && !value.match(/,\s+$/) && limit > 1 ? value + joinDelimiter : value;
  }, [tags, joinDelimiter, limit]);

  // Switch to input mode
  const toInputMode = useCallback(
    (resetSuggestions) => {
      if (isInputMode) return;

      // Populate input with existing tags text when switching to input mode
      const currentTagsText = tags.map((tag) => tag.display || tag.text).join(joinDelimiter);
      if (currentTagsText && !inputValue) {
        setInputValue(currentTagsText);
      }

      setIsInputMode(true);
      if (resetSuggestions) resetSuggestions();

      timeoutRef.current = setTimeout(() => {
        if (textareaRef.current && isMountedRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    },
    [isInputMode, tags, joinDelimiter, inputValue]
  );

  // Switch to tag mode
  const toTagMode = useCallback(() => {
    setIsInputMode(false);
    if (textareaRef.current) {
      textareaRef.current.blur();
    }
  }, []);

  // Handle input change
  const handleInputChange = useCallback((e, splitDelimiter, onCurrentTagChange) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    const newCurrentTag = newValue.split(splitDelimiter).slice(-1)[0];
    setCurrentTag(newCurrentTag);

    if (onCurrentTagChange) {
      onCurrentTagChange(newCurrentTag);
    }
  }, []);

  return {
    inputValue,
    setInputValue,
    isInputMode,
    setIsInputMode,
    currentTag,
    setCurrentTag,
    textareaRef,
    isMountedRef,
    timeoutRef,
    renderTagsToInput,
    toInputMode,
    toTagMode,
    handleInputChange,
  };
};

export default useInputMode;
